/**
 * 历史开奖结果处理辅助函数
 * 提供卡牌处理、牌型显示、动画控制等公共方法
 */

// ========================================
// 📦 常量定义
// ========================================

/**
 * 时间配置常量（统一管理所有时间相关配置）
 */
export const RESULT_TIMING = {
  // 历史结果处理相关
  VUE_UPDATE_WAIT: 100,        // Vue响应式更新等待时间
  DEAL_COMPLETE_WAIT: 200,     // 发牌完成等待时间
  PAUSE_BEFORE_FLIP: 1500,     // 翻牌前停顿时间
  RESULT_DISPLAY_DELAY: 800,   // 结果显示延迟
  FLIP_INTERVAL: 200,          // 依次翻牌间隔
  FLIP_DURATION: 0.4,          // 翻牌动画时长
  
  // 实时开奖动画相关
  FLIP_DELAY: 500,             // 翻牌延迟（毫秒）
  STATUS_DISPLAY: 2000,        // 状态显示时长（毫秒）
  RESULT_FINAL_DELAY: 600      // 最终结果显示延迟（毫秒）
};

/**
 * 区域常量
 */
export const SECTOR_AREAS = ['tian', 'di', 'xuan', 'huang'] as const;
export const ALL_AREAS = [...SECTOR_AREAS, 'banker'] as const;

// 类型定义
export type SectorArea = typeof SECTOR_AREAS[number];
export type AllArea = typeof ALL_AREAS[number];

// ========================================
// 🔧 卡牌处理函数
// ========================================

/**
 * 重新排列卡牌，将10倍数的3张牌排到前面
 * @param cardIds 卡牌ID数组
 * @param getCardsDetailFromIds 获取卡牌详情的函数
 * @param findTenMultipleCards 查找10倍数卡牌的函数
 * @returns 重排后的卡牌数组
 */
export const reorderCardsWithTenMultiple = (
  cardIds: number[],
  getCardsDetailFromIds: (ids: number[]) => any[],
  findTenMultipleCards: (cards: any[]) => number[]
): any[] => {
  let cards = getCardsDetailFromIds(cardIds);
  const tenIndices = findTenMultipleCards(cards);
  
  if (tenIndices.length === 3) {
    const tenCards = tenIndices.map(i => cards[i]);
    const otherCards = cards.filter((_, i) => !tenIndices.includes(i));
    cards = [...tenCards, ...otherCards];
  }
  
  return cards;
};

/**
 * 批量处理所有区域的卡牌（可选择是否重排）
 * @param sectorCardIds 各区域的卡牌ID
 * @param shouldReorder 是否需要重排
 * @param getCardsDetailFromIds 获取卡牌详情的函数
 * @param findTenMultipleCards 查找10倍数卡牌的函数
 * @returns 处理后的区域卡牌数据
 */
export const processAllSectorCards = (
  sectorCardIds: Record<string, number[]>,
  shouldReorder: boolean,
  getCardsDetailFromIds: (ids: number[]) => any[],
  findTenMultipleCards: (cards: any[]) => number[]
): Record<string, any[]> => {
  return SECTOR_AREAS.reduce((result, area) => {
    const cardIds = sectorCardIds[area] || [];
    result[area] = shouldReorder 
      ? reorderCardsWithTenMultiple(cardIds, getCardsDetailFromIds, findTenMultipleCards)
      : getCardsDetailFromIds(cardIds);
    return result;
  }, {} as Record<string, any[]>);
};

/**
 * 获取所有区域的牌型信息
 * @param roundResult 开奖结果数据
 * @param getNiuTypeInfo 获取牌型信息的函数
 * @param rateType 可选的倍率类型（如 'x3', 'x5', 'x10'），用于从 sectorResultsByRoom 获取数据
 * @returns 所有区域的牌型信息
 */
export const getAllHandTypeInfo = (
  roundResult: any,
  getNiuTypeInfo: (data: { niuType: number; multi: any }) => any,
  rateType?: string
) => {
  // 兼容大小写字段名
  const bankerNiuType = roundResult.BankerNiuType ?? roundResult.bankerNiuType ?? 0;
  
  // 尝试从 sectorResultsByRoom 获取区域牌型（服务器返回格式）
  const sectorResults = rateType && roundResult.sectorResultsByRoom 
    ? roundResult.sectorResultsByRoom[rateType] 
    : null;
  // 如果有 sectorResults，从中获取牌型；否则使用顶层字段
  const getTianNiuType = () => {
    if (sectorResults?.tian) return sectorResults.tian.niuType ?? 0;
    return roundResult.TianNiuType ?? roundResult.tianNiuType ?? 0;
  };
  const getDiNiuType = () => {
    if (sectorResults?.di) return sectorResults.di.niuType ?? 0;
    return roundResult.DiNiuType ?? roundResult.diNiuType ?? 0;
  };
  const getXuanNiuType = () => {
    if (sectorResults?.xuan) return sectorResults.xuan.niuType ?? 0;
    return roundResult.XuanNiuType ?? roundResult.xuanNiuType ?? 0;
  };
  const getHuangNiuType = () => {
    if (sectorResults?.huang) return sectorResults.huang.niuType ?? 0;
    return roundResult.HuangNiuType ?? roundResult.huangNiuType ?? 0;
  };

  // 获取倍率
  const getBankerMulti = () => sectorResults?.banker?.multi ?? 1;
  const getTianMulti = () => sectorResults?.tian?.multi ?? 1;
  const getDiMulti = () => sectorResults?.di?.multi ?? 1;
  const getXuanMulti = () => sectorResults?.xuan?.multi ?? 1;
  const getHuangMulti = () => sectorResults?.huang?.multi ?? 1;
  
  // 🔧 修复：getNiuTypeInfo 期望接收对象 { niuType, multi }，而不是两个独立参数
  return {
    banker: getNiuTypeInfo({ niuType: bankerNiuType, multi: getBankerMulti() }),
    tian: getNiuTypeInfo({ niuType: getTianNiuType(), multi: getTianMulti() }),
    di: getNiuTypeInfo({ niuType: getDiNiuType(), multi: getDiMulti() }),
    xuan: getNiuTypeInfo({ niuType: getXuanNiuType(), multi: getXuanMulti() }),
    huang: getNiuTypeInfo({ niuType: getHuangNiuType(), multi: getHuangMulti() })
  };
};

// ========================================
// 🎨 DOM 操作函数
// ========================================

/**
 * 构造卡牌选择器
 * @param area 区域名称
 * @param cardIndex 卡牌索引（0-4）
 * @returns CSS选择器字符串
 */
export const getCardSelector = (area: string, cardIndex: number): string => {
  return area === 'banker'
    ? `.pocker-container .pocker .card:nth-child(${cardIndex + 1})`
    : `.hall-item[data-area="${area}"] .pocker .card:nth-child(${cardIndex + 1})`;
};

/**
 * 批量设置卡牌zIndex（从左到右递增1-5）
 * @param gsap GSAP实例
 * @param areas 需要设置的区域数组
 */
export const setAllCardsZIndex = (gsap: any, areas: string[]) => {
  areas.forEach(area => {
    for (let i = 0; i < 5; i++) {
      const cardEl = document.querySelector(getCardSelector(area, i)) as HTMLElement;
      if (cardEl) {
        gsap.set(cardEl, { zIndex: i + 1 });
      }
    }
  });
};

/**
 * 显示所有区域的牌型
 * @param handTypeInfo 牌型信息对象
 * @param indoorHeaderRef 庄家组件引用
 * @param hallCoverRef 区域组件引用
 */
export const displayAllHandTypes = (
  handTypeInfo: any,
  indoorHeaderRef: any,
  hallCoverRef: any
) => {
  // 显示庄家牌型
  if (indoorHeaderRef?.value) {
    indoorHeaderRef.value.bankerHandType = handTypeInfo.banker.niuType;
    indoorHeaderRef.value.bankerHandName = handTypeInfo.banker.typeName;
    indoorHeaderRef.value.bankerMultiplier = handTypeInfo.banker.multiplier;
    indoorHeaderRef.value.showBettingResult = true;
  }

  // 显示区域牌型
  if (hallCoverRef?.value?.setHandType) {
    SECTOR_AREAS.forEach(area => {
      const info = handTypeInfo[area];
      hallCoverRef.value.setHandType(area, info.typeName, info.niuType, info.multiplier);
    });
  } else {
    console.warn('[displayAllHandTypes] ⚠️ hallCoverRef.value.setHandType 不可用，无法显示区域牌型');
  }
};

// ========================================
// 🎬 动画函数
// ========================================

/**
 * 翻转单张卡牌（GSAP动画）
 * @param gsap GSAP实例
 * @param cardEl 卡牌DOM元素
 * @param duration 动画时长（默认使用RESULT_TIMING.FLIP_DURATION）
 * @returns GSAP时间线或null
 */
export const flipCard = (
  gsap: any, 
  cardEl: Element, 
  duration: number = RESULT_TIMING.FLIP_DURATION
) => {
  const frontEl = cardEl.querySelector('.front') as HTMLElement;
  const backEl = cardEl.querySelector('.back') as HTMLElement;
  
  if (!frontEl || !backEl) return null;
  
  const timeline = gsap.timeline();
  timeline.to(backEl, {
    rotationY: 180,
    duration,
    ease: 'power2.inOut'
  }, 0);
  timeline.fromTo(frontEl,
    { rotationY: -180 },
    { rotationY: 0, duration, ease: 'power2.inOut' },
    0
  );
  
  return timeline;
};

/**
 * 批量翻转卡牌并添加到时间线
 * @param gsap GSAP实例
 * @param areas 需要翻转的区域
 * @param cardIndices 需要翻转的卡牌索引数组
 * @param timeline 目标时间线
 * @param startTime 起始时间
 */
export const flipMultipleCards = (
  gsap: any,
  areas: string[],
  cardIndices: number[],
  timeline: any,
  startTime: number = 0
) => {
  areas.forEach(area => {
    cardIndices.forEach(index => {
      const cardEl = document.querySelector(getCardSelector(area, index));
      if (cardEl) {
        const cardTimeline = flipCard(gsap, cardEl);
        if (cardTimeline) {
          timeline.add(cardTimeline, startTime);
        }
      }
    });
  });
};

// ========================================
// 🛠️ 工具函数
// ========================================

/**
 * 解析扑克牌字符串为ID数组
 * @param cardsStr 卡牌字符串（如 "1,2,3,4,5"）
 * @returns 卡牌ID数组
 */
export const parseCardIds = (cardsStr: string): number[] => {
  return cardsStr ? cardsStr.split(',').map(id => parseInt(id.trim())) : [];
};

/**
 * 解析所有区域的扑克牌数据
 * @param roundResult 开奖结果数据
 * @returns 各区域的卡牌ID
 */
export const parseAllCardIds = (roundResult: any) => ({
  banker: parseCardIds(roundResult.BankerCards),
  tian: parseCardIds(roundResult.TianCards),
  di: parseCardIds(roundResult.DiCards),
  xuan: parseCardIds(roundResult.XuanCards),
  huang: parseCardIds(roundResult.HuangCards)
});

/**
 * 等待指定时间
 * @param ms 等待毫秒数
 * @returns Promise
 */
export const wait = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * 等待Vue nextTick + 额外延迟
 * @param nextTick Vue的nextTick函数
 * @param extraMs 额外等待时间（毫秒）
 */
export const waitForVueUpdate = async (
  nextTick: () => Promise<void>,
  extraMs: number = RESULT_TIMING.VUE_UPDATE_WAIT
): Promise<void> => {
  await nextTick();
  await wait(extraMs);
};
